"use client"

import React, { useEffect, useMemo, useRef, useState, useTransition } from "react"
import { setPreferredInvoiceToAddressAction, setPreferredShipToAddressAction } from "@/actions/accountUserActions"
import { deleteAddressAction, updateAddressAction } from "@/actions/userActions"
import { CheckCircleIcon, FileTextIcon, LoaderCircle, PlusIcon, SearchIcon, TrashIcon, TruckIcon } from "lucide-react"
import { toast } from "sonner"

import { UserData } from "@/lib/intershop/interfaces/user/user"
import { ExtendedAddressRO } from "@/lib/storefront/addresses/address"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/components/alert-dialog"
import { Button } from "@/components/ui/components/button"
import { DataPagination } from "@/components/ui/components/data-pagination"
import { cn } from "@/components/ui/helpers/utils"
import AddAddressButton from "@/components/common/addresses/AddAddressButton"
import AddressEditSheet from "@/components/common/addresses/AddressEditSheet"
import { AddressFields } from "@/components/common/addresses/AddressFields"
import BasicCard from "@/components/common/BasicCard"
import { UbAccountAddressesViewStoryblok } from "@/components/storyblok/component-types"

interface AccountAddressesClientProps {
  blok: UbAccountAddressesViewStoryblok
  addresses: ExtendedAddressRO[]
  customer: any
  user: any
  isB2B: boolean
}

type AddressTypes = "general" | "invoiceTo" | "shipTo"

export default function AccountAddressesClient({
  blok,
  addresses,
  customer,
  user,
  isB2B,
}: AccountAddressesClientProps) {
  const [localAddresses, setLocalAddresses] = useState(addresses)
  const [prefVersion, setPrefVersion] = useState(0)

  // derive preferred addresses from user against current local list to ensure we have full objects
  const preferredInvoice = localAddresses.find((a) => a.id === customer?.preferredInvoiceToAddress?.id)
  const preferredShip = localAddresses.find((a) => a.id === customer?.preferredShipToAddress?.id)
  const equalPreferred = preferredInvoice && preferredShip && preferredInvoice.id === preferredShip.id
  const [preferenceLoading, setPreferenceLoading] = useState("")

  // filtering, sorting, and pagination state
  const [searchQuery, setSearchQuery] = useState("")
  const [page, setPage] = useState(1)
  const perPage = 20
  const [paginationResetKey, setPaginationResetKey] = useState(0)
  const inputRef = useRef<HTMLInputElement | null>(null)

  // deletion state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [toBeDeleted, setToBeDeleted] = useState<string | null>(null)
  const [loadingDelete, deleteTransaction] = useTransition()

  useEffect(() => {
    setPage(1)
    setPaginationResetKey((k) => k + 1)
  }, [searchQuery])

  const normalized = (s?: string) => (s || "").toLowerCase()

  const filteredSortedAddresses = useMemo(() => {
    const q = normalized(searchQuery)
    const includesQ = (a: ExtendedAddressRO) => {
      const parts = [
        a.firstName as any,
        a.lastName as any,
        a.honorific as any,
        a.companyName1 as any,
        a.companyName2 as any,
        a.addressLine1 as any,
        a.addressLine2 as any,
        a.postalCode as any,
        a.city as any,
        a.country as any,
        a.email as any,
        a.phoneBusiness as any,
      ]
        .map(normalized)
        .join(" ")
      return parts.includes(q)
    }

    const list = localAddresses.filter((a) => !q || includesQ(a))
    const prefInvId = preferredInvoice?.id
    const prefShipId = preferredShip?.id
    const rank = (a: ExtendedAddressRO) => {
      if (a.id === prefInvId) return 0
      if (a.id === prefShipId) return 1
      return 2
    }
    const label = (a: ExtendedAddressRO) =>
      normalized(a.companyName1) ||
      `${normalized(a.lastName)} ${normalized(a.firstName)}` ||
      normalized(a.addressLine1) ||
      ""

    return [...list].sort((a, b) => {
      const ra = rank(a)
      const rb = rank(b)
      if (ra !== rb) return ra - rb
      const la = label(a)
      const lb = label(b)
      return la.localeCompare(lb)
    })
  }, [localAddresses, searchQuery, preferredInvoice?.id, preferredShip?.id, prefVersion])

  const totalPages = Math.max(1, Math.ceil(filteredSortedAddresses.length / perPage))
  useEffect(() => {
    if (page > totalPages) setPage(totalPages)
  }, [totalPages])
  const startIndex = (page - 1) * perPage
  const pageItems = filteredSortedAddresses.slice(startIndex, startIndex + perPage)

  async function handleDelete(address: ExtendedAddressRO) {
    if (address.id) {
      setToBeDeleted(address.id)
      setConfirmDialogOpen(true)
    }
  }
  async function resetDelete() {
    setConfirmDialogOpen(false)
    setToBeDeleted(null)
  }
  async function handleConfirmedDelete() {
    deleteTransaction(async () => {
      if (toBeDeleted) {
        await deleteAddressAction(customer.customerNo, toBeDeleted)
        const updatedAddresses = localAddresses.filter((a) => a.id !== toBeDeleted)
        setLocalAddresses(updatedAddresses)
      }
      await resetDelete()
    })
  }

  const handleSave = (newAddress: ExtendedAddressRO, newUser: UserData) => {
    setLocalAddresses((prev) =>
      prev.map((a) => {
        if (a.id === newAddress.id) {
          return newAddress
        } else {
          if (newAddress.preferredShippingAddress === "true") {
            a.preferredShippingAddress = "false"
          }
          if (newAddress.preferredBillingAddress === "true") {
            a.preferredBillingAddress = "false"
          }
          return a
        }
      })
    )
    if (newAddress.preferredShippingAddress === "true") {
      customer && (customer.preferredShipToAddress = newAddress)
    } else {
      if (customer?.preferredShipToAddress?.id === newAddress.id) {
        customer.preferredShipToAddress = null
      }
    }
    if (newAddress.preferredBillingAddress === "true") {
      customer && (customer.preferredInvoiceToAddress = newAddress)
    } else {
      if (customer?.preferredInvoiceToAddress?.id === newAddress.id) {
        customer.preferredInvoiceToAddress = null
      }
    }
  }

  const handleAdd = (newAddress: ExtendedAddressRO, newUser: UserData) => {
    setLocalAddresses((prev) => {
      prev.forEach((a) => {
        if (newAddress.preferredShippingAddress === "true") {
          a.preferredShippingAddress = "false"
        }
        if (newAddress.preferredBillingAddress === "true") {
          a.preferredBillingAddress = "false"
        }
      })
      return [...prev, newAddress]
    })
    if (newAddress.preferredShippingAddress === "true") {
      customer && (customer.preferredShipToAddress = newAddress)
    }
    if (newAddress.preferredBillingAddress === "true") {
      customer && (customer.preferredInvoiceToAddress = newAddress)
    }
  }

  async function handleSetPreferredAddress(address: ExtendedAddressRO, type: "shipTo" | "invoiceTo") {
    if (!address.id) return
    setPreferenceLoading(address.id + type)
    switch (type) {
      case "shipTo":
        try {
          await setPreferredShipToAddressAction(address.urn, user, customer)
          address.preferredShippingAddress = "true"
          const updated = await updateAddressAction(customer.customerNo, address.id, address)
          if (updated) {
            setLocalAddresses((prev) =>
              prev.map((a) => {
                if (a.id === address.id) {
                  return address
                } else {
                  if (address.preferredShippingAddress === "true") {
                    a.preferredShippingAddress = "false"
                  }
                  return a
                }
              })
            )
          }
          // optimistic update of local user preference if available
          // @ts-ignore
          customer && (customer.preferredShipToAddress = updated ?? address)
          setPrefVersion((v) => v + 1)
          toast.success(blok.preferred_shipping_address_toast_success, {
            icon: <CheckCircleIcon className="size-4 text-green-500" />,
          })
        } catch (e) {
          console.error("Failed to update preferred ship-to address and backend address", e)
          toast.error(blok.preferred_shipping_address_toast_error)
        }
        break
      case "invoiceTo":
        try {
          await setPreferredInvoiceToAddressAction(address.urn, user, customer)
          address.preferredBillingAddress = "true"
          const updated = await updateAddressAction(customer.customerNo, address.id, address)
          if (updated) {
            setLocalAddresses((prev) =>
              prev.map((a) => {
                if (a.id === address.id) {
                  return address
                } else {
                  if (address.preferredBillingAddress === "true") {
                    a.preferredBillingAddress = "false"
                  }
                  return a
                }
              })
            )
          }
          // optimistic update
          // @ts-ignore
          customer && (customer.preferredInvoiceToAddress = updated ?? address)
          setPrefVersion((v) => v + 1)
          toast.success(blok.preferred_billing_address_toast_success, {
            icon: <CheckCircleIcon className="size-4 text-green-500" />,
          })
        } catch (e) {
          console.error("Failed to update preferred invoice address and backend address", e)
          toast.error(blok.preferred_billing_address_toast_error)
        }
        break
    }
    setPreferenceLoading("")
  }

  return (
    <div className={"flex flex-col gap-6"}>
      {/* Preferred Address Sections (merged when equal) */}
      {equalPreferred ? (
        <div className="">
          <h2 className="mb-2 text-xl font-semibold">{blok.preferred_billing_and_shipping_address}</h2>
          {preferredInvoice ? (
            <BasicCard className={"flex min-h-0 w-full flex-row gap-x-6 text-sm md:w-auto"}>
              <div className="flex w-full flex-col gap-3">
                <AddressFields
                  address={preferredInvoice}
                  isB2B={isB2B}
                  showEligibleIcons={{
                    invoiceAddressTooltip: String(blok.invoice_address_tooltip),
                    shippingAddressTooltip: String(blok.shipping_address_tooltip),
                  }}
                />
              </div>
              <div className={cn("flex w-5 flex-col items-end gap-y-2")}>
                <AddressEditSheet
                  address={preferredInvoice}
                  customer={customer}
                  user={user}
                  mode={"edit"}
                  type={(preferredInvoice.type as AddressTypes) ?? "invoiceTo"}
                  onSave={(a, u) => handleSave(a, u)}
                />
              </div>
            </BasicCard>
          ) : (
            <p className="text-gray-700">{blok.preferred_billing_and_shipping_address_placeholder}</p>
          )}
        </div>
      ) : (
        <>
          {/* Preferred Invoice Address Section */}
          <div className="">
            <h2 className="mb-2 text-xl font-semibold">{blok.preferred_billing_address}</h2>
            {preferredInvoice ? (
              <BasicCard className={"flex min-h-0 w-full flex-row gap-x-6 text-sm md:w-auto"}>
                <div className="flex w-full flex-col gap-3">
                  <AddressFields
                    address={preferredInvoice}
                    isB2B={isB2B}
                    showEligibleIcons={{
                      invoiceAddressTooltip: String(blok.invoice_address_tooltip),
                      shippingAddressTooltip: String(blok.shipping_address_tooltip),
                    }}
                  />
                </div>
                <div className={cn("flex w-5 flex-col items-end gap-y-2")}>
                  <AddressEditSheet
                    address={preferredInvoice}
                    customer={customer}
                    user={user}
                    mode={"edit"}
                    type={(preferredInvoice.type as AddressTypes) ?? "invoiceTo"}
                    onSave={(a, u) => handleSave(a, u)}
                  />
                </div>
              </BasicCard>
            ) : (
              <p className="text-gray-700">{blok.preferred_billing_address_placeholder}</p>
            )}
          </div>

          {/* Preferred Ship Address Section */}
          <div className="">
            <h2 className="mb-2 text-xl font-semibold">{blok.preferred_shipping_address}</h2>
            {preferredShip ? (
              <BasicCard className={"flex min-h-0 w-full flex-row gap-x-6 text-sm md:w-auto"}>
                <div className="flex w-full flex-col gap-3">
                  <AddressFields
                    address={preferredShip}
                    isB2B={isB2B}
                    showEligibleIcons={{
                      invoiceAddressTooltip: String(blok.invoice_address_tooltip),
                      shippingAddressTooltip: String(blok.shipping_address_tooltip),
                    }}
                  />
                </div>
                <div className={cn("flex w-5 flex-col items-end gap-y-2")}>
                  <AddressEditSheet
                    address={preferredShip}
                    customer={customer}
                    user={user}
                    mode={"edit"}
                    type={(preferredShip.type as AddressTypes) ?? "shipTo"}
                    onSave={(a, u) => handleSave(a, u)}
                  />
                </div>
              </BasicCard>
            ) : (
              <p className="text-gray-700">{blok.preferred_shipping_address_placeholder}</p>
            )}
          </div>
        </>
      )}

      <div className={"flex flex-col gap-4"}>
        <h2 className="-mb-2 text-xl font-semibold">{blok.all_addresses}</h2>
        {/* Search / Filter */}
        <div className="rounded-sm border border-gray-300 bg-gray-200">
          <div className="grid grid-rows-1 gap-2 p-4 lg:grid-cols-[min-content,_auto,_max-content]">
            <div className="relative h-fit w-full md:pr-0 lg:w-96">
              {searchQuery != "" ? (
                <button
                  onClick={() => {
                    setSearchQuery("")
                    inputRef.current?.focus()
                  }}
                  className={"absolute right-2 top-1/2 size-5 -translate-y-1/2 text-gray-400"}
                >
                  <PlusIcon strokeWidth={2} className={"size-5 rotate-45"} />
                </button>
              ) : (
                <div className={"pointer-events-none absolute right-2 top-1/2 size-5 -translate-y-1/2 text-gray-400"}>
                  <SearchIcon strokeWidth={2} className="size-5" />
                </div>
              )}
              <input
                type="text"
                placeholder={blok.search_placeholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                ref={inputRef}
                className="w-full rounded-lg border border-gray-300 py-2 pl-4 pr-10 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className={"max-lg:hidden"} />
            <div className={"flex max-lg:justify-center"}>
              <AddAddressButton
                customer={customer}
                user={user}
                type={"general"}
                onAdd={handleAdd}
                context={"account"}
              />
            </div>
          </div>
        </div>

        {/* All Addresses (sorted, filtered, paginated) */}
        <div className="grid auto-rows-max grid-cols-1 gap-2 lg:grid-cols-2">
          {pageItems.length === 0 ? (
            <div className="col-span-1 text-gray-700 lg:col-span-2">{blok.all_addresses_placeholder}</div>
          ) : (
            pageItems.map((address) => (
              <BasicCard key={address.id} className={"relative grid grid-cols-1 grid-rows-[max-content_auto] text-sm"}>
                <div className={"flex w-full flex-row gap-x-6"}>
                  <div className="flex w-full flex-col gap-3">
                    <AddressFields
                      address={address}
                      isB2B={isB2B}
                      showEligibleIcons={{
                        invoiceAddressTooltip: String(blok.invoice_address_tooltip),
                        shippingAddressTooltip: String(blok.shipping_address_tooltip),
                      }}
                    />
                    {/*<div className="absolute -top-3 left-2 flex min-h-6 flex-wrap gap-2">
                        {address.id === preferredInvoice?.id && (
                          <Badge variant="secondary" className="h-6 whitespace-nowrap text-xs">
                            {blok.preferred_billing_address_badge}
                          </Badge>
                        )}
                        {address.id === preferredShip?.id && (
                          <Badge variant="secondary" className="h-6 whitespace-nowrap text-xs">
                            {blok.preferred_shipping_address_badge}
                          </Badge>
                        )}
                      </div>*/}
                  </div>
                  <div className={cn("flex w-5 flex-col items-end gap-y-2")}>
                    <AddressEditSheet
                      address={address}
                      customer={customer}
                      user={user}
                      mode={"edit"}
                      type={(address.type as AddressTypes) ?? "general"}
                      onSave={(a, u) => handleSave(a, u)}
                    />
                    {address.id !== preferredInvoice?.id && address.id !== preferredShip?.id && (
                      <button
                        onClick={() => handleDelete(address)}
                        className="mt-2 text-destructive hover:text-destructive/80"
                        title={blok.delete_address_button}
                      >
                        <TrashIcon className="size-5" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex w-full items-end justify-end">
                  <div className={"flex h-fit w-full flex-wrap justify-end gap-2"}>
                    {address.shipToAddress && (
                      <Button
                        variant={customer?.preferredShipToAddress?.id === address.id ? "outline" : "default"}
                        size="smNoGrow"
                        disabled={customer?.preferredShipToAddress?.id === address.id || preferenceLoading != ""}
                        onClick={() => handleSetPreferredAddress(address, "shipTo")}
                        className={cn("gap-0.5 whitespace-nowrap", {
                          "border-green-500 text-green-500": customer?.preferredShipToAddress?.id === address.id,
                        })}
                      >
                        {preferenceLoading === address.id + "shipTo" ? (
                          <LoaderCircle className={"inline h-5 w-fit motion-safe:animate-spin"} />
                        ) : (
                          <TruckIcon className={"inline h-5 w-fit"} />
                        )}
                        {blok.preferred_shipping_address_button}
                      </Button>
                    )}
                    {address.invoiceToAddress && (
                      <Button
                        variant={customer?.preferredInvoiceToAddress?.id === address.id ? "outline" : "default"}
                        size="smNoGrow"
                        disabled={customer?.preferredInvoiceToAddress?.id === address.id || preferenceLoading != ""}
                        onClick={() => handleSetPreferredAddress(address, "invoiceTo")}
                        className={cn("gap-0.5 whitespace-nowrap", {
                          "border-green-500 text-green-500": customer?.preferredInvoiceToAddress?.id === address.id,
                        })}
                      >
                        {preferenceLoading === address.id + "invoiceTo" ? (
                          <LoaderCircle className={"inline h-5 w-fit motion-safe:animate-spin"} />
                        ) : (
                          <FileTextIcon className={"inline h-5 w-fit"} />
                        )}
                        {blok.preferred_billing_address_button}
                      </Button>
                    )}
                  </div>
                </div>
              </BasicCard>
            ))
          )}
        </div>

        {/* Pagination */}
        <div className="flex w-full justify-center">
          <DataPagination
            key={`addr-pg-${paginationResetKey}`}
            initialPage={page}
            totalPages={totalPages}
            onPageChange={(p) => setPage(p)}
          />
        </div>
      </div>
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen} defaultOpen={false}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{blok.alert_title}</AlertDialogTitle>
            <AlertDialogDescription>{blok.alert_message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={resetDelete} disabled={loadingDelete}>
              {blok.common_cancel}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmedDelete} disabled={loadingDelete}>
              {loadingDelete ? <LoaderCircle className={"motion-safe:animate-spin"} /> : blok.common_confirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
